import React from "react";
import { useQuote } from "../context/QuoteContext";
import { StorMiLogo } from "./StorMiLogo";
import { Button } from "./ui/button";
import { Undo2, Settings, LogOut, ArrowLeft, HelpCircle, User, ChevronDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "./ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const MODE_LABELS = {
  objects: "Per oggetti", area: "Per area", area_done: "Per area",
  text: "Da testo", text_done: "Da testo", image: "Da immagine", image_done: "Da immagine",
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function Header({ onOpenSettings, onOpenGuide }) {
  const { canUndo, undo, undoDescription, resetQuote, logout, mode, setMode, user } = useQuote();

  const isAdmin = user?.role === "admin";
  const displayName = user?.email ? cap(user.email.split("@")[0].replace(/[._-]+/g, " ")) : "Utente";

  return (
    <header className="sticky top-0 z-50 w-full bg-[#1a5f5a] text-white shadow-md">
      <div className="max-w-[1150px] mx-auto w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        <StorMiLogo dark onClick={resetQuote} />
        <div className="flex items-center gap-1 sm:gap-2">
          <TooltipProvider delayDuration={200}>
            {canUndo && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={undo} data-testid="undo-button"
                    className="text-white hover:bg-white/15 hover:text-white gap-1.5">
                    <Undo2 className="h-4 w-4" /> <span className="hidden sm:inline">Annulla</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{undoDescription}</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
          <Button variant="ghost" size="sm" onClick={onOpenGuide} data-testid="guide-button"
            className="text-white hover:bg-white/15 hover:text-white gap-1.5">
            <HelpCircle className="h-4 w-4" /> <span className="hidden sm:inline">Guida</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={onOpenSettings} data-testid="settings-button"
            className="text-white hover:bg-white/15 hover:text-white gap-1.5">
            <Settings className="h-4 w-4" /> <span className="hidden sm:inline">Impostazioni</span>
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="user-menu-button"
                className="text-white hover:bg-white/15 hover:text-white gap-1.5 pl-1.5">
                <span className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <User className="h-4 w-4" />
                </span>
                <span className="hidden sm:inline max-w-[140px] truncate font-medium">{displayName}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-0 text-slate-800" data-testid="user-menu-popover">
              <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-100">
                <span className="h-10 w-10 rounded-full bg-[#1a5f5a]/10 text-[#1a5f5a] flex items-center justify-center shrink-0">
                  <User className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate" data-testid="user-menu-name">{displayName}</div>
                  <div className="text-xs text-slate-500 truncate" data-testid="user-menu-email">{user?.email}</div>
                </div>
              </div>
              <div className="px-4 py-3 space-y-2 text-xs border-b border-slate-100">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Dati personali</div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Ruolo</span>
                  <span className={`font-medium px-2 py-0.5 rounded-full ${isAdmin ? "bg-[#1a5f5a]/10 text-[#134744]" : "bg-slate-100 text-slate-600"}`} data-testid="user-menu-role">
                    {isAdmin ? "Amministratore" : "Utente"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Preventivi</span>
                  <span className="font-medium text-slate-700">{user?.can_view_all_quotes ? "Vede tutti" : "Solo i propri + condivisi"}</span>
                </div>
                {!isAdmin && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Sezioni consentite</span>
                    <span className="font-medium text-slate-700" data-testid="user-menu-sections">{(user?.allowed_sections || []).length}</span>
                  </div>
                )}
              </div>
              <div className="p-1.5">
                <Button variant="ghost" onClick={logout} data-testid="user-menu-logout"
                  className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 h-9">
                  <LogOut className="h-4 w-4" /> Esci
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {mode && (
        <div className="bg-[#134744]">
          <div className="max-w-[1150px] mx-auto w-full px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3">
            <button onClick={() => setMode(null)} data-testid="back-button"
              className="flex items-center gap-1 text-sm text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" /> Indietro
            </button>
            <span className="text-xs uppercase tracking-[0.2em] bg-white/10 text-white px-2.5 py-1 rounded-full">
              {MODE_LABELS[mode] || mode}
            </span>
          </div>
        </div>
      )}
    </header>
  );
}
